import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip
} from '@mui/material';
import dayjs from 'dayjs';
import { CalendarEvent, Listing } from '../../schemas/calendar';
import { getColorByType } from '../../utils/StatusColors';

interface DayViewProps {
  currentDate: Date;
  events: CalendarEvent[];
  listings: Listing[];
  onEventClick: (event: CalendarEvent) => void;
  onSlotClick: (start: Date, end: Date, listingId: string) => void;
}

const DayView: React.FC<DayViewProps> = ({
  currentDate,
  events,
  listings,
  onEventClick,
  onSlotClick
}) => {

  const timeSlots = Array.from({ length: 24 }, (_, i) => i);
  const currentDay = dayjs(currentDate);
  const TIME_COL_PX = 72;
  const MIN_LANE_WIDTH = 140;
  const laneCount = Math.max(listings.length, 1);
  const gridTemplateColumns = `${TIME_COL_PX}px repeat(${laneCount}, minmax(${MIN_LANE_WIDTH}px, 1fr))`;
  const gridMinWidth = TIME_COL_PX + laneCount * MIN_LANE_WIDTH;

  const getEventsForListingAndHour = (listingId: string, hour: number) => {
    const filteredEvents = events.filter(event => {
      const eventStart = dayjs(event.startTime);
      const eventEnd = dayjs(event.endTime);
      
      // Only show the event in the hour it starts, not in every hour it spans
      return (
        event.listingId === listingId &&
        eventStart.isSame(currentDay, 'day') &&
        eventStart.hour() === hour
      );
    });
    return filteredEvents;
  };

  const getEventHeight = (event: CalendarEvent) => {
    const start = dayjs(event.startTime);
    const end = dayjs(event.endTime);
    const durationInHours = end.diff(start, 'minute') / 60;
    return Math.max(durationInHours * 60, 30); // Minimum 30px height
  };

  return (
    <Box sx={{ height: '100%', overflow: 'auto', WebkitOverflowScrolling: 'touch' }}>
      <Box sx={{ minWidth: gridMinWidth }}>
      {/* Header with Listings */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns,
          borderBottom: '2px solid',
          borderColor: 'grey.200',
          position: 'sticky',
          top: 0,
          backgroundColor: 'background.default',
          zIndex: 10
        }}
      >
        <Box
          sx={{
            p: { xs: 1, md: 2 },
            borderRight: '1px solid',
            borderColor: 'grey.200',
            position: 'sticky',
            left: 0,
            zIndex: 11,
            backgroundColor: 'background.default',
          }}
        >
          <Typography
            variant="subtitle2"
            fontWeight={600}
            color="primary"
            sx={{ fontSize: { xs: '0.75rem', md: '1.25rem' } }}
          >
            Time
          </Typography>
        </Box>
        {listings.map((listing) => (
          <Box
            key={listing.id}
            sx={{
              p: { xs: 1, md: 2 },
              borderRight: '1px solid',
              borderColor: 'grey.200',
              textAlign: 'center',
              background: `linear-gradient(135deg, ${listing.color}15, ${listing.color}05)`
            }}
          >
            <Typography
              variant="subtitle2"
              fontWeight={600}
              color="primary"
              sx={{
                fontSize: { xs: '0.75rem', md: '1.25rem' },
                lineHeight: 1.2,
                wordBreak: 'break-word',
              }}
            >
              {listing.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {listing.type.replace('_', ' ').toUpperCase()}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Time Grid */}
      <Box sx={{ position: 'relative' }}>
        {timeSlots.map((hour) => (
          <Box
            key={hour}
            sx={{
              display: 'grid',
              gridTemplateColumns,
              minHeight: 60,
              borderBottom: '1px solid',
              borderColor: 'grey.100',
              '&:hover': {
                backgroundColor: 'grey.50'
              }
            }}
          >
            {/* Time Label */}
            <Box
              sx={{
                p: { xs: 0.5, md: 1 },
                borderRight: '1px solid',
                borderColor: 'grey.200',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
                position: 'sticky',
                left: 0,
                zIndex: 2,
                backgroundColor: 'background.paper',
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', md: '0.75rem' } }}>
                {dayjs().hour(hour).format('h A')}
              </Typography>
            </Box>

            {/* Listing Columns */}
            {listings.map((listing) => {
              const hourEvents = getEventsForListingAndHour(listing.id, hour);
              return (
                <Box
                  key={`${listing.id}-${hour}`}
                  sx={{
                    position: 'relative',
                    borderRight: '1px solid',
                    borderColor: 'grey.200',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: `${listing.color}10`
                    }
                  }}
                  // onClick={() => {
                  //   const start = currentDay.hour(hour).minute(0).second(0).toDate();
                  //   const end = currentDay.hour(hour + 1).minute(0).second(0).toDate();
                  //   onSlotClick(start, end, listing.id);
                  // }}
                >
                  {hourEvents.map((event) => {
                    const eventStart = dayjs(event.startTime);
                    const minuteOffset = eventStart.minute();
                    const topOffset = (minuteOffset / 60) * 60;
                    
                    return (
                      <Paper
                        key={event.id}
                        elevation={2}
                        sx={{
                          position: 'absolute',
                          top: topOffset,
                          left: 4,
                          right: 4,
                          height: getEventHeight(event),
                          // backgroundColor: getEventColor(event, listing),
                          backgroundColor: getColorByType(listing),
                          // color: 'white',
                          p: 1,
                          cursor: 'pointer',
                          zIndex: 1,
                          '&:hover': {
                            transform: 'scale(1.02)',
                            boxShadow: '0 8px 10px rgba(124, 58, 237, 0.3)'
                          },
                          transition: 'all 0.3s ease',
                          overflow: 'hidden'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onEventClick(event);
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            display: 'block',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          {event.title}
                        </Typography>
                        {event.customerName && (
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              opacity: 0.9,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                          >
                            {event.customerName}
                          </Typography>
                        )}
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            opacity: 0.8,
                            fontSize: '0.7rem'
                          }}
                        >
                          {eventStart.format('h:mm A')} - {dayjs(event.endTime).format('h:mm A')}
                        </Typography>
                        {event.status !== 'confirmed' && (
                          <Chip
                            label={event.status}
                            size="small"
                            sx={{
                              height: 16,
                              fontSize: '0.6rem',
                              backgroundColor: 'rgba(255,255,255,0.3)',
                              mt: 0.5
                            }}
                          />
                        )}
                      </Paper>
                    );
                  })}
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>
      </Box>
    </Box>
  );
};

export default DayView;

// Better UI Day View

// import React, { useState } from 'react';
// import {
//   Box,
//   Typography,
//   Paper,
//   Chip,
//   useTheme,
//   alpha,
//   Tooltip,
//   IconButton,
//   Stack
// } from '@mui/material';
// import {
//   Add,
//   Person,
//   Schedule,
//   AttachMoney,
//   FiberManualRecord
// } from '@mui/icons-material';
// import dayjs from 'dayjs';
// import { CalendarEvent, Listing } from '../../schemas/calendar';
// import { getColorByType } from '../../utils/StatusColors';

// interface DayViewProps {
//   currentDate: Date;
//   events: CalendarEvent[];
//   listings: Listing[];
//   onEventClick: (event: CalendarEvent) => void;
//   onSlotClick: (start: Date, end: Date, listingId: string) => void;
// }

// const DayView: React.FC<DayViewProps> = ({
//   currentDate,
//   events,
//   listings,
//   onEventClick,
//   onSlotClick
// }) => {
//   const theme = useTheme();
//   const [hoveredSlot, setHoveredSlot] = useState<string | null>(null);

//   const timeSlots = Array.from({ length: 24 }, (_, i) => i);
//   const currentDay = dayjs(currentDate);
//   const currentHour = dayjs().hour();
//   const isToday = currentDay.isSame(dayjs(), 'day');

//   const getEventsForListingAndHour = (listingId: string, hour: number) => {
//     const filteredEvents = events.filter(event => {
//       const eventStart = dayjs(event.startTime);
//       const eventEnd = dayjs(event.endTime);
      
//       return (
//         event.listingId === listingId &&
//         eventStart.isSame(currentDay, 'day') &&
//         eventStart.hour() === hour
//       );
//     });
//     return filteredEvents;
//   };

//   const getEventHeight = (event: CalendarEvent) => {
//     const start = dayjs(event.startTime);
//     const end = dayjs(event.endTime);
//     const durationInHours = end.diff(start, 'minute') / 60;
//     return Math.max(durationInHours * 70, 40); // Increased minimum height
//   };

//   const getStatusColor = (status: string) => {
//     switch (status) {
//       case 'confirmed': return theme.palette.success.main;
//       case 'pending': return theme.palette.warning.main;
//       case 'cancelled': return theme.palette.error.main;
//       default: return theme.palette.grey[500];
//     }
//   };

//   const EventCard = ({ event, listing }: { event: CalendarEvent; listing: Listing }) => {
//     const eventStart = dayjs(event.startTime);
//     const eventEnd = dayjs(event.endTime);
//     const duration = eventEnd.diff(eventStart, 'minute');
    
//     return (
//       <Tooltip
//         title={
//           <Box sx={{ p: 1 }}>
//             <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
//               {event.title}
//             </Typography>
//             {event.description && (
//               <Typography variant="caption" sx={{ display: 'block', mb: 1, opacity: 0.9 }}>
//                 {event.description}
//               </Typography>
//             )}
//             <Stack spacing={0.5}>
//               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//                 <Schedule sx={{ fontSize: 14 }} />
//                 <Typography variant="caption">
//                   {eventStart.format('h:mm A')} - {eventEnd.format('h:mm A')} ({duration}min)
//                 </Typography>
//               </Box>
//               {event.customerName && (
//                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//                   <Person sx={{ fontSize: 14 }} />
//                   <Typography variant="caption">{event.customerName}</Typography>
//                 </Box>
//               )}
//               {event.price && (
//                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//                   <AttachMoney sx={{ fontSize: 14 }} />
//                   <Typography variant="caption">${event.price}/hr</Typography>
//                 </Box>
//               )}
//             </Stack>
//           </Box>
//         }
//         placement="top"
//         arrow
//       >
//         <Paper
//           elevation={0}
//           sx={{
//             position: 'absolute',
//             top: (eventStart.minute() / 60) * 70,
//             left: 6,
//             right: 6,
//             height: getEventHeight(event),
//             background: `linear-gradient(135deg, ${getColorByType(listing)} 0%, ${alpha(getColorByType(listing), 0.8)} 100%)`,
//             color: theme.palette.getContrastText(getColorByType(listing)),
//             p: 1.5,
//             cursor: 'pointer',
//             zIndex: 2,
//             border: `2px solid ${alpha(getColorByType(listing), 0.3)}`,
//             borderRadius: 2,
//             transition: 'all 0.2s ease-in-out',
//             '&:hover': {
//               transform: 'translateY(-2px) scale(1.02)',
//               boxShadow: `0 8px 25px ${alpha(getColorByType(listing), 0.4)}`,
//               zIndex: 3
//             },
//             overflow: 'hidden',
//             // position: 'relative',
//             '&::before': {
//               content: '""',
//               position: 'absolute',
//               top: 0,
//               left: 0,
//               width: '4px',
//               height: '100%',
//               backgroundColor: getStatusColor(event.status),
//               borderRadius: '2px 0 0 2px'
//             }
//           }}
//           onClick={(e) => {
//             e.stopPropagation();
//             onEventClick(event);
//           }}
//         >
//           <Box sx={{ pl: 1 }}>
//             <Typography
//               variant="caption"
//               sx={{
//                 fontWeight: 700,
//                 display: 'block',
//                 whiteSpace: 'nowrap',
//                 overflow: 'hidden',
//                 textOverflow: 'ellipsis',
//                 fontSize: '0.8rem',
//                 mb: 0.5
//               }}
//             >
//               {event.title}
//             </Typography>
            
//             {event.customerName && getEventHeight(event) > 50 && (
//               <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
//                 <Person sx={{ fontSize: 12, opacity: 0.8 }} />
//                 <Typography
//                   variant="caption"
//                   sx={{
//                     display: 'block',
//                     opacity: 0.9,
//                     whiteSpace: 'nowrap',
//                     overflow: 'hidden',
//                     textOverflow: 'ellipsis',
//                     fontSize: '0.7rem'
//                   }}
//                 >
//                   {event.customerName}
//                 </Typography>
//               </Box>
//             )}
            
//             <Typography
//               variant="caption"
//               sx={{
//                 display: 'block',
//                 opacity: 0.8,
//                 fontSize: '0.65rem',
//                 fontWeight: 500
//               }}
//             >
//               {eventStart.format('h:mm A')} - {eventEnd.format('h:mm A')}
//             </Typography>
            
//             {event.status !== 'confirmed' && getEventHeight(event) > 70 && (
//               <Chip
//                 label={event.status.toUpperCase()}
//                 size="small"
//                 sx={{
//                   height: 18,
//                   fontSize: '0.6rem',
//                   backgroundColor: alpha(theme.palette.common.white, 0.2),
//                   color: 'inherit',
//                   mt: 0.5,
//                   fontWeight: 600
//                 }}
//               />
//             )}
//           </Box>
//         </Paper>
//       </Tooltip>
//     );
//   };

//   const TimeSlot = ({ hour, listing }: { hour: number; listing: Listing }) => {
//     const slotId = `${listing.id}-${hour}`;
//     const hourEvents = getEventsForListingAndHour(listing.id, hour);
//     const isPastHour = isToday && hour < currentHour;
//     const isCurrentHour = isToday && hour === currentHour;
//     const isHovered = hoveredSlot === slotId;

//     return (
//       <Box
//         key={slotId}
//         sx={{
//           position: 'relative',
//           borderRight: `1px solid ${theme.palette.divider}`,
//           minHeight: 70,
//           cursor: 'pointer',
//           backgroundColor: isPastHour 
//             ? alpha(theme.palette.grey[300], 0.3)
//             : isCurrentHour
//             ? alpha(theme.palette.primary.main, 0.05)
//             : 'transparent',
//           transition: 'all 0.2s ease-in-out',
//           '&:hover': {
//             backgroundColor: isPastHour 
//               ? alpha(theme.palette.grey[300], 0.4)
//               : alpha(listing.color || theme.palette.primary.main, 0.08)
//           },
//           '&::before': isCurrentHour ? {
//             content: '""',
//             position: 'absolute',
//             left: 0,
//             top: 0,
//             bottom: 0,
//             width: '3px',
//             backgroundColor: theme.palette.primary.main,
//             zIndex: 1
//           } : {}
//         }}
//         onMouseEnter={() => setHoveredSlot(slotId)}
//         onMouseLeave={() => setHoveredSlot(null)}
//         onClick={() => {
//           const start = currentDay.hour(hour).minute(0).second(0).toDate();
//           const end = currentDay.hour(hour + 1).minute(0).second(0).toDate();
//           onSlotClick(start, end, listing.id);
//         }}
//       >
//         {/* Add Event Button on Hover */}
//         {isHovered && hourEvents.length === 0 && (
//           <Box
//             sx={{
//               position: 'absolute',
//               top: '50%',
//               left: '50%',
//               transform: 'translate(-50%, -50%)',
//               zIndex: 1
//             }}
//           >
//             <IconButton
//               size="small"
//               sx={{
//                 backgroundColor: alpha(theme.palette.primary.main, 0.1),
//                 color: theme.palette.primary.main,
//                 '&:hover': {
//                   backgroundColor: alpha(theme.palette.primary.main, 0.2)
//                 }
//               }}
//             >
//               <Add fontSize="small" />
//             </IconButton>
//           </Box>
//         )}

//         {/* Events */}
//         {hourEvents.map((event) => (
//           <EventCard key={event.id} event={event} listing={listing} />
//         ))}
//       </Box>
//     );
//   };

//   return (
//     <Box sx={{ 
//       height: '100%', 
//       overflow: 'auto',
//       backgroundColor: theme.palette.background.default
//     }}>
//       {/* Enhanced Header */}
//       <Paper
//         elevation={2}
//         sx={{
//           display: 'grid',
//           gridTemplateColumns: `100px repeat(${listings.length}, 1fr)`,
//           position: 'sticky',
//           top: 0,
//           zIndex: 10,
//           backgroundColor: theme.palette.background.paper,
//           borderRadius: 0,
//           borderBottom: `3px solid ${theme.palette.primary.main}`
//         }}
//       >
//         <Box 
//           sx={{ 
//             p: 2.5, 
//             borderRight: `1px solid ${theme.palette.divider}`,
//             display: 'flex',
//             alignItems: 'center',
//             justifyContent: 'center',
//             background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.1)} 100%)`
//           }}
//         >
//           <Typography variant="h6" fontWeight={700} color="primary">
//             <Schedule sx={{ mr: 1, fontSize: '1.2rem' }} />
//             Time
//           </Typography>
//         </Box>
        
//         {listings.map((listing) => (
//           <Box
//             key={listing.id}
//             sx={{
//               p: 2.5,
//               borderRight: `1px solid ${theme.palette.divider}`,
//               textAlign: 'center',
//               background: `linear-gradient(135deg, ${alpha(listing.color || theme.palette.primary.main, 0.08)} 0%, ${alpha(listing.color || theme.palette.primary.main, 0.02)} 100%)`,
//               position: 'relative',
//               '&::before': {
//                 content: '""',
//                 position: 'absolute',
//                 bottom: 0,
//                 left: '20%',
//                 right: '20%',
//                 height: '3px',
//                 backgroundColor: listing.color || theme.palette.primary.main,
//                 borderRadius: '2px 2px 0 0'
//               }
//             }}
//           >
//             <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.5 }}>
//               <FiberManualRecord 
//                 sx={{ 
//                   fontSize: 12, 
//                   color: listing.color || theme.palette.primary.main,
//                   mr: 1 
//                 }} 
//               />
//               <Typography variant="h6" fontWeight={700} color="text.primary">
//                 {listing.name}
//               </Typography>
//             </Box>
//             <Chip
//               label={listing.type.replace('_', ' ').toUpperCase()}
//               size="small"
//               variant="outlined"
//               sx={{
//                 fontSize: '0.65rem',
//                 height: 22,
//                 borderColor: listing.color || theme.palette.primary.main,
//                 color: listing.color || theme.palette.primary.main,
//                 fontWeight: 600
//               }}
//             />
//           </Box>
//         ))}
//       </Paper>

//       {/* Enhanced Time Grid */}
//       <Box sx={{ position: 'relative', backgroundColor: theme.palette.background.paper }}>
//         {timeSlots.map((hour) => (
//           <Box
//             key={hour}
//             sx={{
//               display: 'grid',
//               gridTemplateColumns: `100px repeat(${listings.length}, 1fr)`,
//               borderBottom: `1px solid ${theme.palette.divider}`,
//               '&:nth-of-type(even)': {
//                 backgroundColor: alpha(theme.palette.grey[50], 0.5)
//               }
//             }}
//           >
//             {/* Enhanced Time Label */}
//             <Box
//               sx={{
//                 p: 2,
//                 borderRight: `1px solid ${theme.palette.divider}`,
//                 display: 'flex',
//                 flexDirection: 'column',
//                 alignItems: 'center',
//                 justifyContent: 'center',
//                 backgroundColor: isToday && hour === currentHour 
//                   ? alpha(theme.palette.primary.main, 0.1)
//                   : 'transparent',
//                 position: 'relative'
//               }}
//             >
//               <Typography 
//                 variant="body2" 
//                 fontWeight={600}
//                 color={isToday && hour === currentHour ? 'primary' : 'text.secondary'}
//               >
//                 {dayjs().hour(hour).format('h:mm')}
//               </Typography>
//               <Typography 
//                 variant="caption" 
//                 color={isToday && hour === currentHour ? 'primary' : 'text.secondary'}
//                 sx={{ fontSize: '0.7rem' }}
//               >
//                 {dayjs().hour(hour).format('A')}
//               </Typography>
              
//               {/* Current time indicator */}
//               {isToday && hour === currentHour && (
//                 <Box
//                   sx={{
//                     position: 'absolute',
//                     right: -1,
//                     top: '50%',
//                     transform: 'translateY(-50%)',
//                     width: 8,
//                     height: 8,
//                     borderRadius: '50%',
//                     backgroundColor: theme.palette.primary.main,
//                     zIndex: 2
//                   }}
//                 />
//               )}
//             </Box>

//             {/* Listing Time Slots */}
//             {listings.map((listing) => (
//               <TimeSlot key={listing.id} hour={hour} listing={listing} />
//             ))}
//           </Box>
//         ))}
//       </Box>
//     </Box>
//   );
// };

// export default DayView;